import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  Container, Paper, Stack, Typography, Box,
  Button, TextField, Alert, IconButton, InputAdornment,
  CircularProgress, Link, Backdrop
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { 
  createUserWithEmailAndPassword, 
  sendEmailVerification,
  signOut,
  Auth,
  User
} from "firebase/auth";
import { auth as defaultAuth } from '../firebase';
import { logEvent } from '../analytics';

interface CreatePasswordPageProps {
  authInstance?: Auth;
  onLoginSuccess?: () => void;
}

const getFirebaseErrorMessage = (error: any): string => {
  const code = error?.code;
  switch (code) {
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Please sign in instead.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters long.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    default:
      return error?.message || 'Failed to create account. Please try again.';
  }
};

const CreatePasswordPage: React.FC<CreatePasswordPageProps> = ({ 
  authInstance, 
  onLoginSuccess 
}) => {
  const auth = authInstance || defaultAuth;
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [unverifiedUser, setUnverifiedUser] = useState<User | null>(null);

  React.useEffect(() => {
    document.title = "Jets '14 | Create Password";
  }, []);

  const handleAuthResult = async (user: User) => {
    setLoading(true);
    try {
      await user.reload();
      if (!user.emailVerified) {
        setLoading(false);
        setUnverifiedUser(user);
        return;
      }
      setLoading(false);
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    } catch (e) {
      setLoading(false);
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    }
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!email || !password) {
      setError('Please provide an email and password.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }

    setLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      await sendEmailVerification(userCredential.user);
      logEvent('sign_up', { method: 'Email_Password' });
      setMessage('Account created! Please check your email to verify your address.');
      setUnverifiedUser(userCredential.user);
      setLoading(false);
    } catch (err: any) {
      console.error("Create password error:", err);
      setError(getFirebaseErrorMessage(err));
      logEvent('sign_up_error', { method: 'Email_Password', error: err?.code });
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedUser) return;
    setLoading(true);
    setError(null);
    try {
      await sendEmailVerification(unverifiedUser);
      setMessage(`Verification email resent to ${unverifiedUser.email}! Please check your inbox.`);
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification email.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationCheck = async () => {
    if (!unverifiedUser) return;
    setLoading(true);
    setError(null);
    try {
      await unverifiedUser.reload();
      if (unverifiedUser.emailVerified) {
        setUnverifiedUser(null);
        await handleAuthResult(unverifiedUser);
      } else {
        setLoading(false);
        setError('Email is still not verified. Please check your inbox and click the verification link.');
      }
    } catch (err: any) {
      setLoading(false);
      setError('Failed to check verification status. Please try again.');
    }
  };

  const handleCancelVerification = async () => {
    await signOut(auth);
    setUnverifiedUser(null);
    setError(null);
    setMessage(null);
    navigate('/');
  };

  // Verification Screen
  if (unverifiedUser) {
    return (
      <Container component="main" maxWidth="xs">
        <Paper 
          elevation={3}
          sx={{
            marginTop: 6,
            marginBottom: 6,
            padding: { xs: 3, sm: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2,
          }}
        >
          <Box sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', p: 1.5, borderRadius: '50%', mb: 2 }}>
            <MarkEmailReadIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>

          <Typography component="h1" variant="h5" textAlign="center" fontWeight="bold" gutterBottom>
            Verify Your Email
          </Typography>
          <Typography variant="body2" textAlign="center" sx={{ mb: 2, color: 'text.secondary' }}>
            We've sent a verification link to <strong>{unverifiedUser.email}</strong>. 
            Please click the link in that email to verify your account before accessing the team portal.
          </Typography>
          
          {error && <Alert severity="error" sx={{ mt: 1, mb: 2, width: '100%' }}>{error}</Alert>}
          {message && <Alert severity="success" sx={{ mt: 1, mb: 2, width: '100%' }}>{message}</Alert>}

          <Button
            fullWidth
            variant="contained"
            disableElevation
            onClick={handleVerificationCheck}
            disabled={loading}
            sx={{ mt: 2, mb: 1, py: 1 }}
          >
            {loading ? <CircularProgress size={24} /> : "I've verified my email"}
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={handleResendVerification}
            disabled={loading}
            sx={{ mb: 1, py: 0.8 }}
          >
            Resend Verification Email
          </Button>

          <Button
            fullWidth
            onClick={handleCancelVerification}
            color="inherit"
            size="small"
            sx={{ mt: 1 }}
          >
            Back to Sign In
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Paper 
        elevation={3}
        sx={{
          marginTop: { xs: 3, sm: 6 },
          marginBottom: { xs: 3, sm: 6 },
          padding: { xs: 2.5, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: 2,
        }}
      >
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Box
            component="img"
            src={process.env.PUBLIC_URL + '/chicago_jets_shield_logo.jpg'} 
            alt="Chicago Jets Logo" 
            sx={{ height: { xs: 65, sm: 80 }, width: 'auto' }} 
          />
          <Box
            component="img"
            src={process.env.PUBLIC_URL + '/csdhl_logo.png'} 
            alt="CSDHL Logo" 
            sx={{ height: { xs: 65, sm: 80 }, width: 'auto' }} 
          />
        </Stack>
        
        <Typography 
          component="h1" 
          variant="h5" 
          textAlign="center" 
          fontWeight="bold"
          sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' } }}
        >
          Create a Password
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2, mt: 0.5 }}>
          Use the email address registered on the team roster to create a password for your account.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}

        {message && (
          <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
            {message}
          </Alert>
        )}

        <Box component="form" onSubmit={handleCreateAccount} noValidate sx={{ width: '100%' }}>
          <TextField
            margin="dense"
            required
            fullWidth
            id="signup-email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            size="small"
          />
          <TextField
            margin="dense"
            required
            fullWidth
            name="password"
            label="Password (min 6 characters)"
            type={showPassword ? 'text' : 'password'}
            id="signup-password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            required
            fullWidth
            name="confirmPassword"
            label="Confirm Password"
            type={showPassword ? 'text' : 'password'}
            id="signup-confirm-password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ mt: 2, mb: 1.5, py: 1, textTransform: 'none', fontWeight: 600 }}
          >
            {loading ? <CircularProgress size={24} /> : 'Create Password & Verify'}
          </Button>
          
          <Box textAlign="center" sx={{ mt: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              Already have a password?{' '}
              <Link component={RouterLink} to="/password-signin" sx={{ fontWeight: 500 }}>
                Sign In
              </Link>
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <Link component={RouterLink} to="/" color="text.secondary" sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                ← Back to Google / Email sign in
              </Link>
            </Typography>
          </Box>
        </Box>
      </Paper>
      
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};

export default CreatePasswordPage;
